import { Thunk, thunk } from 'easy-peasy';
import { AxiosResponse } from 'axios';
import { HttpStoreModel } from '../../../http/models/httpModel';
import { AccountController } from '../../../controllers/accountController';
import HttpClientWithRetry from '../../../http/client/HttpClientWithRetry';
import { BackgroundChecks } from '../../../firebase/firestore/documents/candidate';
import { CertnFormData, SuccessMessageResponse, UploadLogoResponse } from '../certn/CertnType';
import { ModernHireConfigData, ModernHireUpdateResponseType } from '../ModernHireConfigView/ModernHireConfigData';

export type AdministrationStoreModel = {
  administration: AdministrationModel;
};

interface WorkBrightResponse {
  data?: {
    message?: string;
  };
}

export interface AdministrationModel {
  saveCompanyLogo: Thunk<
    AdministrationModel,
    { accountId: string; companyLogo: string; isSquare: boolean },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<UploadLogoResponse>>
  >;

  getCompanyLogo: Thunk<AdministrationModel, { accountId: string; isSquare: boolean }, void, HttpStoreModel, Promise<string>>;

  saveCertnSecrets: Thunk<
    AdministrationModel,
    { accountId: string; apiKey: string; webhookSignature: string },
    void,
    HttpStoreModel,
    Promise<SuccessMessageResponse>
  >;
  getCertnSecrets: Thunk<AdministrationModel, { accountId: string }, void, HttpStoreModel, Promise<CertnFormData>>;
  getWorkBrightCredentials: Thunk<AdministrationModel, { accountId: string }, void, HttpStoreModel, Promise<any>>;
  saveWorkBrightCredentials: Thunk<
    AdministrationModel,
    { accountId: string; apiKey: string; subDomain: string; username: string; password: string; webhookSignature?: string },
    void,
    HttpStoreModel,
    Promise<any>
  >;

  administrationBackgroundCheck: Thunk<
    AdministrationModel,
    { accountId: string; backgroundCheckList: BackgroundChecks },
    void,
    HttpStoreModel,
    Promise<SuccessMessageResponse>
  >;
  getHireFormAPIKey: Thunk<AdministrationModel, { accountId: string; isRenew: boolean }, void, HttpStoreModel, Promise<string>>;
  getModernHireConfigData: Thunk<AdministrationModel, { accountId: string }, void, HttpStoreModel, Promise<ModernHireConfigData>>;
  updateModernHireConfigData: Thunk<
    AdministrationModel,
    { accountId: string; configData: ModernHireConfigData },
    void,
    HttpStoreModel,
    Promise<ModernHireUpdateResponseType>
  >;
}

export const administrationStoreModel: AdministrationModel = {
  saveCompanyLogo: thunk((_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    return controller.uploadLogo(payload.accountId, payload.companyLogo, payload.isSquare);
  }),

  getCompanyLogo: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getCompanyLogo(payload.accountId, payload.isSquare);
    return response;
  }),

  saveCertnSecrets: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.saveCertnSecrets(payload.accountId, payload.apiKey, payload.webhookSignature);
    return response;
  }),

  getCertnSecrets: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getCertnSecrets(payload.accountId);
    return response;
  }),

  administrationBackgroundCheck: thunk(async (actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.administrationBackgroundCheck(payload.accountId, payload.backgroundCheckList);
    return response;
  }),

  getHireFormAPIKey: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getHireFormAPIKey(payload.accountId, payload.isRenew);
    return response;
  }),

  getWorkBrightCredentials: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getWorkBrightCredentials(payload.accountId);
    return response;
  }),

  saveWorkBrightCredentials: thunk(async (_actions, payload, { getStoreState }) => {
    const { accountId, apiKey, subDomain, username, password } = payload;
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.saveWorkBrightCredentials(accountId, {
      apiKey,
      subDomain,
      username,
      password,
    });
    return response;
  }),

  getModernHireConfigData: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.getModernHireConfigData(payload.accountId);
    return response;
  }),

  updateModernHireConfigData: thunk(async (_actions, payload, { getStoreState }) => {
    const controller = new AccountController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.updateModernHireConfigData(payload.accountId, payload.configData);
    return response;
  }),
};
